import * as React from "react";

import {
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { debounce } from "@mui/material/utils";

import { ICON_SIZE } from "@dashboard/components/common/StreamlineIcon";
import { PinIcon } from "@dashboard/components/common/icons";
import { SearchIcon } from "@dashboard/components/common/icons";
import { config } from "@dashboard/environment/config";
import type { CountryNameAndCode } from "@dashboard/queries/countries";

import type { FoundRestaurant, PlaceDetailResult } from "./gmap-utils";
import { parsePlaceDetailResult } from "./gmap-utils";

const gMapsApiKey = config.googleMaps.apiKey;
// place types that we search for
// these are places which might sell food. upto 5 types can be specified
const types = ["bar", "bakery", "cafe", "restaurant", "night_club"];
const detailFields = [
  "address_components",
  "name",
  "geometry",
  "opening_hours",
  "price_level",
  "website",
  "international_phone_number",
];

type PlaceServiceStaus =
  | "INVALID_REQUEST"
  | "NOT_FOUND"
  | "OK"
  | "OVER_QUERY_LIMIT"
  | "REQUEST_DENIED"
  | "UNKNOWN_ERROR"
  | "ZERO_RESULTS";

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
  place_id: string;
  description: string;
  structured_formatting: StructuredFormatting;
}

export const GMapAutoComplete = ({
  onSelect,
  setLoading,
  restrictions,
}: {
  onSelect: (rest: FoundRestaurant) => void;
  setLoading: (loading: boolean) => void;
  restrictions: CountryNameAndCode[];
}) => {
  const [value, setValue] = React.useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);
  const loaded = React.useRef(false);

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${gMapsApiKey}&libraries=places`,
        document.querySelector("head"),
        "google-maps"
      );
    }

    loaded.current = true;
  }
  const countryRestrictions = React.useMemo(
    () => restrictions.map((r) => r.code),
    [restrictions]
  );
  const fetch = React.useMemo(
    () =>
      debounce(
        (
          request: {
            input: string;
            componentRestrictions: { country: string[] };
            types: string[];
          },
          callback: (results?: readonly PlaceType[]) => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback
          );
        },
        400
      ),
    []
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch(
      {
        input: inputValue,
        componentRestrictions: {
          country: countryRestrictions,
        },
        types,
      },
      (results?: readonly PlaceType[]) => {
        if (active) {
          let newOptions: readonly PlaceType[] = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      }
    );

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, countryRestrictions]);

  const fetchPlaceDetails = React.useMemo(
    () =>
      (
        request: {
          placeId: string;
          fields: string[];
        },
        callback: (
          results: PlaceDetailResult,
          status: PlaceServiceStaus
        ) => void
      ) => {
        const service = new (window as any).google.maps.places.PlacesService(
          document.createElement("div")
        );
        service.getDetails(request, callback);
      },
    []
  );

  const onSelected = React.useCallback(
    (place: PlaceType) => {
      setLoading(true);
      fetchPlaceDetails(
        {
          placeId: place.place_id,
          fields: detailFields,
        },
        (result, status) => {
          status === "OK" && onSelect(parsePlaceDetailResult(result));
          setLoading(false);
        }
      );
    },
    [onSelect, fetchPlaceDetails, setLoading]
  );

  return (
    <Autocomplete
      id="google-map-search"
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText="No restaurant found"
      onChange={(_, newValue: PlaceType | null, reason) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (reason === "selectOption" && newValue) {
          onSelected(newValue);
        }
      }}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search Restaurant"
          fullWidth
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon size={ICON_SIZE.medium} />
              </InputAdornment>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <MenuItem {...props} key={option.place_id}>
          <ListItemIcon>
            <PinIcon size={20} />
          </ListItemIcon>
          <ListItemText
            primary={option.structured_formatting.main_text}
            secondary={option.structured_formatting.secondary_text}
          />
        </MenuItem>
      )}
    />
  );
};
